/**
 * EndTextNode - Node component for 'end-text' type fields
 * 
 * Displays ending text that can optionally jump to another section.
 * Has a source handle on the right for connections.
 */

import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { DialogField } from '@/lib/stores/dialogStore';
import { BaseDialogNode } from './BaseDialogNode';
import { DialogFieldNodeData, getFieldTypeIcon, getFieldTypeColor, getSectionName } from './dialogNodeUtils';
import { useDisplayLanguageOptional, resolveFieldDisplayText, resolveFieldEditableText, saveFieldTranslation } from '@/contexts/DisplayLanguageContext';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

export const EndTextNode = memo(({ id, data, selected }: NodeProps<any>) => {
  const nodeData = data as DialogFieldNodeData;
  const field = nodeData.field;
  const { selectedGame } = useSimplifiedUserData();
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const effectiveGameId = selectedGame?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editedType, setEditedType] = useState(field.type);
  const [editedText, setEditedText] = useState(field.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevSelectedRef = useRef(selected);

  // Attach native event listeners to prevent dragging
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !isEditing) return;

    const handlePointerDown = (e: PointerEvent) => {
      e.stopImmediatePropagation();
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.stopImmediatePropagation();
    };

    textarea.addEventListener('pointerdown', handlePointerDown, { capture: true });
    textarea.addEventListener('mousedown', handleMouseDown, { capture: true });
    
    return () => {
      textarea.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      textarea.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [isEditing]);

  // Sync local state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedType(field.type);
      setEditedText(field.text);
    }
  }, [field, isEditing]);

  // Are we editing a translation (non-source language + TMS-linked field)?
  const isTranslationMode = displayLanguage !== null && !!field.string_id && !!field.translatable;

  const handleSaveChanges = useCallback(() => {
    if (isTranslationMode && field.string_id && effectiveGameId && displayLanguage) {
      saveFieldTranslation(field.string_id, displayLanguage, editedText, effectiveGameId);
      if (editedType !== field.type && nodeData.onFieldUpdate) {
        nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, { type: editedType });
      }
    } else if (nodeData.onFieldUpdate) {
      nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, {
        type: editedType,
        text: editedText,
      });
    }
    setIsEditing(false);
  }, [editedType, editedText, nodeData, isTranslationMode, field.string_id, field.type, displayLanguage, effectiveGameId]);

  // Auto-save when node loses selection (clicking outside)
  useEffect(() => {
    // Only auto-save if we're transitioning from selected to unselected while editing
    if (prevSelectedRef.current && !selected && isEditing) {
      handleSaveChanges();
    }
    // Update ref for next render
    prevSelectedRef.current = selected;
  }, [selected, isEditing, handleSaveChanges]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
      setEditedType(field.type);
      setEditedText(resolveFieldEditableText(field, displayLanguage, effectiveGameId));
    }
  }, [isEditing, handleSaveChanges, field, displayLanguage, effectiveGameId]);

  const colors = getFieldTypeColor(isEditing ? editedType : field.type);

  // Header content for edit mode (with type selector)
  const headerContent = isEditing ? (
    <>
      <IconProvider iconstring={getFieldTypeIcon(editedType)} size={14} className={colors.text} />
      <Select value={editedType} onValueChange={(value: any) => setEditedType(value)}>
        <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dialog">Dialog</SelectItem>
          <SelectItem value="question">Question</SelectItem>
          <SelectItem value="context">Context</SelectItem>
          <SelectItem value="end-dialog">End Dialog</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : undefined;

  return (
    <BaseDialogNode
      id={id}
      data={nodeData}
      selected={selected}
      isEditing={isEditing}
      onEditToggle={handleEditToggle}
      headerContent={headerContent}
    >
      {/* Source Handle - right side for next connection (end-text can still connect) */}
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />

      {/* End text content */}
      {isEditing ? (
        <Textarea
          ref={textareaRef}
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          className="text-sm mb-2 min-h-[60px] noDrag"
          placeholder="Enter ending text..."
        />
      ) : (
        <div className="text-sm mb-2 line-clamp-3">
          {resolveFieldDisplayText(field, displayLanguage, effectiveGameId) || '(empty)'}
        </div>
      )}

      {/* Field-level Jump Indicator */}
      {!isEditing && (field.next_section || field.next_dialog) && (
        <div className={`mt-2 mb-2 pt-2 border-t ${field.next_dialog ? 'border-orange-200' : 'border-purple-200'}`}>
          <div className={`flex items-center gap-2 text-xs p-1.5 rounded ${
            field.next_dialog 
              ? 'bg-orange-50 border border-orange-200' 
              : 'bg-purple-50 border border-purple-200'
          }`}>
            <IconProvider 
              iconstring={field.next_dialog ? "fa-arrow-up-right-from-square" : "fa-arrow-right"} 
              size={10} 
              className={field.next_dialog ? 'text-orange-600' : 'text-purple-600'}
            />
            <span className={field.next_dialog ? 'text-orange-700' : 'text-purple-700'}>
              {field.next_dialog 
                ? `→ External: ${field.next_dialog.substring(0, 8)}...${field.next_dialog_section ? ` (${field.next_dialog_section})` : ''}` 
                : `→ Section: ${getSectionName(field.next_section, nodeData.sections)}`
              }
            </span>
          </div>
        </div>
      )}

      {/* Metadata badges */}
      {!isEditing && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          {field.translatable && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              <IconProvider iconstring="fa-language" size={10} />
              <span>Translatable</span>
            </div>
          )}
        </div>
      )}
    </BaseDialogNode>
  );
});

EndTextNode.displayName = 'EndTextNode';

export default EndTextNode;
